import { useMemo, useRef, useState } from "react";
import type { StrategyGraph } from "@/lib/types";
import {
  layoutGraph,
  type EdgeCategory,
  type LayoutEdge,
  type LayoutNode,
  type PositionOverrides,
} from "@/lib/graphLayout";
import { cn } from "@/lib/cn";

/**
 * The configured phase graph, drawn.
 *
 * <p>Edges are an SVG layer; phases are HTML buttons positioned over it, which
 * keeps them focusable, screen-reader friendly, and able to wrap their own text
 * — all things hand-rolled SVG labels do badly. Selecting a phase raises its
 * connected transitions so an operator can trace one path through a dense graph.
 *
 * <p>Colour encodes what a transition <em>does</em> — carry on, skip ahead, go
 * back, or point at nothing — because that is the question being asked of the
 * picture. It is derived from the graph's shape, so a strategy from any niche
 * colours correctly without the renderer knowing a single phase name. Colour is
 * never the only signal: every edge carries a visible label, and broken ones are
 * additionally dashed and flagged.
 */
export default function FlowchartView({
  graph,
  selectedSlug,
  onSelect,
  className,
  positions,
  onMove,
}: {
  graph: StrategyGraph;
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
  className?: string;
  /** Saved/draft node positions by phase slug; unlisted phases use the derived layout. */
  positions?: PositionOverrides;
  /** When set, phases are draggable and each finished drag reports its position. */
  onMove?: (slug: string, pos: { x: number; y: number }) => void;
}) {
  // A drag in flight overrides the overrides — the node follows the pointer,
  // and only the finished position is reported upward.
  const [dragPos, setDragPos] = useState<{ slug: string; x: number; y: number } | null>(null);
  const effectivePositions = useMemo<PositionOverrides | undefined>(() => {
    if (!dragPos) return positions;
    return { ...positions, [dragPos.slug]: { x: dragPos.x, y: dragPos.y } };
  }, [positions, dragPos]);

  const layout = useMemo(
    () => layoutGraph(graph, effectivePositions),
    [graph, effectivePositions],
  );

  // Parallel lanes of the same category are identical strokes; hovering an
  // edge (or its label) raises that one flow end-to-end so it can be traced
  // without following the line by eye. Complements, and composes with, the
  // click-selection of a phase.
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  // A phase nothing points at is where prospects enter the flow.
  const entrySlugs = useMemo(() => {
    const targeted = new Set(graph.edges.map((e) => e.to).filter(Boolean));
    return new Set(graph.nodes.filter((n) => !targeted.has(n.slug)).map((n) => n.slug));
  }, [graph]);

  if (graph.nodes.length === 0) {
    return (
      <p className="text-sm text-[var(--fg-muted)]">
        This strategy declares no phases yet.
      </p>
    );
  }

  const isRaised = (edge: LayoutEdge) =>
    edge.key === hoveredKey ||
    (selectedSlug !== null && (edge.from === selectedSlug || edge.to === selectedSlug));
  const anyFocus = hoveredKey !== null || selectedSlug !== null;

  return (
    <div className={cn("overflow-auto", className)}>
      <div
        className="relative"
        style={{ width: layout.width, height: layout.height }}
        data-testid="flowchart-canvas"
      >
        <svg
          width={layout.width}
          height={layout.height}
          className="absolute inset-0"
          style={{ pointerEvents: "none" }}
          aria-hidden
        >
          <defs>
            {CATEGORIES.map((category) => (
              <marker
                key={category}
                id={`df-arrow-${category}`}
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="5"
                markerHeight="5"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={COLOR[category]} />
              </marker>
            ))}
          </defs>

          {layout.edges.map((edge) => {
            const active = isRaised(edge);
            const dimmed = anyFocus && !active && !edge.dangling;
            return (
              <g key={edge.key}>
                <path
                  d={edge.path}
                  fill="none"
                  stroke={COLOR[edge.category]}
                  strokeWidth={active ? 2.75 : 1.75}
                  strokeLinecap="round"
                  strokeDasharray={edge.dangling ? "5 4" : undefined}
                  markerEnd={`url(#df-arrow-${edge.category})`}
                  opacity={dimmed ? 0.25 : 1}
                  className="transition-opacity"
                />
                {/* Invisible fat twin: a 1.75px stroke is no hover target. */}
                <path
                  d={edge.path}
                  fill="none"
                  stroke="transparent"
                  strokeWidth={14}
                  style={{ pointerEvents: "stroke" }}
                  onPointerEnter={() => setHoveredKey(edge.key)}
                  onPointerLeave={() =>
                    setHoveredKey((current) => (current === edge.key ? null : current))
                  }
                />
              </g>
            );
          })}
        </svg>

        {layout.edges.map((edge) => (
          <EdgeLabel
            key={edge.key}
            edge={edge}
            active={isRaised(edge)}
            dimmed={anyFocus && !isRaised(edge) && !edge.dangling}
            onHover={(hovering) =>
              setHoveredKey((current) =>
                hovering ? edge.key : current === edge.key ? null : current,
              )
            }
          />
        ))}

        {layout.nodes.map((node) => (
          <PhaseNode
            key={node.slug}
            node={node}
            selected={node.slug === selectedSlug}
            isEntry={entrySlugs.has(node.slug)}
            onSelect={onSelect}
            onDrag={onMove ? setDragPos : undefined}
            onDragEnd={
              onMove
                ? (slug, pos) => {
                    setDragPos(null);
                    onMove(slug, pos);
                  }
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

const CATEGORIES: EdgeCategory[] = ["advance", "branch", "return", "broken"];

/**
 * Validated categorical trio plus a reserved status colour, defined once in
 * globals.css. Read through CSS variables so the palette has a single home.
 */
const COLOR: Record<EdgeCategory, string> = {
  advance: "var(--flow-advance)",
  branch: "var(--flow-branch)",
  return: "var(--flow-return)",
  broken: "var(--flow-broken)",
};

const CATEGORY_LABEL: Record<EdgeCategory, string> = {
  advance: "continues to the next phase",
  branch: "skips ahead",
  return: "goes back",
  broken: "target does not exist",
};

function EdgeLabel({
  edge,
  active,
  dimmed,
  onHover,
}: {
  edge: LayoutEdge;
  active: boolean;
  dimmed: boolean;
  onHover: (hovering: boolean) => void;
}) {
  const detail = [
    CATEGORY_LABEL[edge.category],
    edge.to ? `→ ${edge.to}` : null,
    edge.description,
    edge.setStatus ? `sets status ${edge.setStatus}` : null,
    edge.requiresHumanApproval ? "requires human approval" : null,
    edge.kind === "compound" ? `priority ${edge.priority}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={cn(
        "absolute -translate-y-1/2 whitespace-nowrap transition-opacity",
        edge.labelAnchor === "end" && "-translate-x-full",
        edge.labelAnchor === "middle" && "-translate-x-1/2",
      )}
      style={{ left: edge.labelX, top: edge.labelY, opacity: dimmed ? 0.3 : 1 }}
      onPointerEnter={() => onHover(true)}
      onPointerLeave={() => onHover(false)}
    >
      <span
        title={detail}
        className={cn(
          "flex items-center gap-1 rounded-full border bg-[var(--bg-card)] px-2 py-0.5",
          "text-[11px] leading-tight shadow-sm",
          active ? "font-medium" : "",
        )}
        style={{
          borderColor: COLOR[edge.category],
          color: edge.dangling ? COLOR.broken : "var(--fg)",
        }}
      >
        {edge.requiresHumanApproval ? (
          <span aria-hidden title="requires human approval">⏸</span>
        ) : null}
        {/* Written out in full: the label *is* the transition rule the operator
            came to read, and the layout reserves lane room for its measured
            width (estimateLabelWidth), so nothing is clipped or overrun. */}
        <span>{edge.label}</span>
        {/* Inline, not on a second line: a two-line chip overlaps the next
            transition's label as soon as a phase has more than one exit. */}
        {edge.setStatus ? (
          <span className="shrink-0 text-[10px] text-[var(--fg-muted)]">
            ·&nbsp;{edge.setStatus}
          </span>
        ) : null}
        {edge.dangling ? <span aria-hidden>⚠</span> : null}
      </span>
    </div>
  );
}

/** Pointer movement below this is a click; above it, a drag. */
const DRAG_THRESHOLD_PX = 4;

function PhaseNode({
  node,
  selected,
  isEntry,
  onSelect,
  onDrag,
  onDragEnd,
}: {
  node: LayoutNode;
  selected: boolean;
  isEntry: boolean;
  onSelect: (slug: string) => void;
  onDrag?: (pos: { slug: string; x: number; y: number }) => void;
  onDragEnd?: (slug: string, pos: { x: number; y: number }) => void;
}) {
  // The accent states the phase's role in the flow: where prospects come in,
  // where they stop, and everything in between.
  const accent = isEntry
    ? "var(--flow-advance)"
    : node.terminal
      ? "var(--fg-muted)"
      : "var(--border)";

  // Drag state lives in a ref: pointermove must not re-render this button —
  // the parent re-lays-out the whole graph per move already.
  const drag = useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);

  const draggable = onDrag !== undefined && onDragEnd !== undefined;

  return (
    <button
      type="button"
      onClick={() => {
        // A finished drag must not double as a click on release.
        if (drag.current?.moved) return;
        onSelect(node.slug);
      }}
      onPointerDown={(e) => {
        if (!draggable || e.button !== 0) return;
        drag.current = {
          pointerId: e.pointerId,
          startClientX: e.clientX,
          startClientY: e.clientY,
          originX: node.x,
          originY: node.y,
          moved: false,
        };
        // Absent in jsdom; a drag in a real browser always has it.
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }}
      onPointerMove={(e) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) return;
        const dx = e.clientX - d.startClientX;
        const dy = e.clientY - d.startClientY;
        if (!d.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        d.moved = true;
        onDrag?.({
          slug: node.slug,
          x: Math.max(0, d.originX + dx),
          y: Math.max(0, d.originY + dy),
        });
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) return;
        if (d.moved) {
          const dx = e.clientX - d.startClientX;
          const dy = e.clientY - d.startClientY;
          onDragEnd?.(node.slug, {
            x: Math.max(0, d.originX + dx),
            y: Math.max(0, d.originY + dy),
          });
          // Keep `moved` for the click handler that fires right after release,
          // then clear on the next tick.
          setTimeout(() => { drag.current = null; }, 0);
        } else {
          drag.current = null;
        }
      }}
      onPointerCancel={() => { drag.current = null; }}
      aria-pressed={selected}
      aria-label={`Phase ${node.name}`}
      className={cn(
        "absolute flex flex-col overflow-hidden rounded-xl border bg-[var(--bg-card)]",
        "p-2.5 pl-3 text-left transition",
        selected
          ? "border-[var(--flow-advance)] shadow-md ring-2 ring-blue-200"
          : "border-[var(--border)] shadow-sm hover:border-zinc-400 hover:shadow",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
        draggable && "cursor-grab touch-none active:cursor-grabbing",
      )}
      style={{ left: node.x, top: node.y, width: node.w, height: node.h }}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5"
        style={{ background: accent }}
      />

      <div className="flex items-center gap-2">
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
            "text-[10px] font-semibold tabular-nums",
            node.terminal
              ? "bg-zinc-100 text-zinc-500"
              : "bg-blue-50 text-[var(--flow-advance)]",
          )}
        >
          {node.order}
        </span>
        <span className="truncate text-sm font-semibold">{node.name}</span>
        {isEntry ? (
          <span className="ml-auto shrink-0 rounded-full bg-blue-50 px-1.5 text-[10px] text-[var(--flow-advance)]">
            start
          </span>
        ) : null}
        {node.terminal ? (
          <span className="ml-auto shrink-0 rounded-full bg-zinc-100 px-1.5 text-[10px] text-zinc-600">
            end
          </span>
        ) : null}
      </div>

      <code className="mt-0.5 truncate text-[10px] text-[var(--fg-muted)]">{node.slug}</code>
      {node.goal ? (
        <span className="mt-0.5 truncate text-xs text-[var(--fg-muted)]">{node.goal}</span>
      ) : null}

      <div className="mt-auto flex flex-wrap items-center gap-1 pt-1">
        {node.activeManualPrompts > 0 ? (
          <Badge tone="amber">
            {node.activeManualPrompts} prompt{node.activeManualPrompts === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {node.hasDocumentOutput ? <Badge tone="violet">document</Badge> : null}
        {node.qualificationQuestionCount > 0 ? (
          <Badge tone="sky">
            {node.qualificationQuestionCount} question
            {node.qualificationQuestionCount === 1 ? "" : "s"}
          </Badge>
        ) : null}
        {node.aiRuleCount > 0 ? <Badge tone="emerald">{node.aiRuleCount} rules</Badge> : null}
        {node.ragTopics.length > 0 ? (
          <Badge tone="zinc">{node.ragTopics.length} topics</Badge>
        ) : null}
      </div>
    </button>
  );
}

const TONES = {
  zinc: "border-zinc-200 bg-zinc-50 text-zinc-600",
  amber: "border-amber-200 bg-amber-50 text-amber-800",
  violet: "border-violet-200 bg-violet-50 text-violet-800",
  sky: "border-sky-200 bg-sky-50 text-sky-800",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
} as const;

function Badge({ children, tone }: { children: React.ReactNode; tone: keyof typeof TONES }) {
  return (
    <span className={cn("rounded border px-1 py-0 text-[10px] leading-4", TONES[tone])}>
      {children}
    </span>
  );
}

/** Explains the colour encoding. Exported so the page can place it. */
export function FlowchartLegend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-[var(--fg-muted)]">
      {CATEGORIES.map((category) => (
        <span key={category} className="flex items-center gap-1.5">
          <svg width="22" height="8" aria-hidden>
            <line
              x1="0"
              y1="4"
              x2="22"
              y2="4"
              stroke={COLOR[category]}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={category === "broken" ? "4 3" : undefined}
            />
          </svg>
          {CATEGORY_LABEL[category]}
        </span>
      ))}
      <span className="flex items-center gap-1.5">⏸ requires human approval</span>
    </div>
  );
}
